import { Box } from '@mui/material'
import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'

import SaveRecipeDialog from '@/components/SaveRecipeDialog'
import WorkFlowTab from '@/components/WorkFlowTab'
import NewKonvaCanvas from '@/components/canvas/NewKonvaCanvas'
import ImageEdit from '@/components/imageEdit/ImageEdit'
import { useCanvasSize } from '@/hooks/useCanvasSize'
import useGenerateImage from '@/hooks/useGenerateImage'
import CaseInfoLayout from '@/layout/CaseInfoLayout'
import { useAppSelector } from '@/store'
import {
  useGenerateDentinRecipeMutation,
  useSavePdfMutation,
  useSaveRecipeMutation,
} from '@/store/apiService'
import { LABColor } from '@/store/appSlice'
import {
  setDentinColorTarget,
  setSelectedDentinColorKey,
  setdentinRecipeOutput,
} from '@/store/pfmCrown'
import {
  ImageCalibrationStatus,
  setImageCalibrationStatus,
} from '@/store/sessionSlice'
import { getCaseId } from '@/utils'
import { Workflows } from '@/utils/constants'
import {
  getDehazedImageUrl,
  hasNonEmptyOutputSingleCanvas,
  prepareSelectedDentinOutput,
  saveRecipeGeneric,
} from '@/utils/helper'

import { imageType } from '../../allInOne/utils/constants'
import DentinPanel from './panel/DentinPanel'

function index() {
  const { t } = useTranslation('translation', {
    keyPrefix: 'dentin',
  })
  const { t: translation } = useTranslation()
  const { containerRef, dimensions } = useCanvasSize()
  const combinedRef = useRef<HTMLDivElement>(null)

  const { generateImage } = useGenerateImage()
  const caseId = getCaseId() || ''

  const [saveRecipedialogOpen, setSaveRecipedialogOpen] = useState(false)
  const [recipeName, setRecipeName] = useState('')

  const dispatch = useDispatch()

  const canvasImage = useAppSelector((state) => state.app.canvasImage)

  const imageCalibrationStatus = useAppSelector(
    (state) => state.session.imageCalibrationStatus,
  )

  const selectedDentinColorKey = useAppSelector(
    (state) => state.pfmCrown.selectedDentinColorKey,
  )

  const dentinCeramicBrand = useAppSelector(
    (state) => state.pfmCrown.dentinCeramicBrand,
  )

  const greyCardValue = useAppSelector((state) => state.app.greyCardValue)

  const dentinRecipeOutput = useAppSelector(
    (state) => state.pfmCrown.dentinRecipeOutput,
  )
  const path = useLocation().pathname
  const imageEdit = useAppSelector(
    (state) => state.app.imageEdit[path] || false,
  )
  const dentinRecipeIndexByRegion = useAppSelector(
    (state) => state.app.dentinRecipeIndexByRegion?.['pfmCrown'],
  )

  const [generateDentinRecipe] = useGenerateDentinRecipeMutation()
  const [saveRecipe, { isLoading }] = useSaveRecipeMutation()
  const [savePdf] = useSavePdfMutation()

  const onImageClick = (
    _type: string,
    _placement: string,
    imagePos: { x: number; y: number },
    _hiddenCanvasRef: any,
    labColor: LABColor,
  ) => {
    if (
      canvasImage?.id &&
      imageCalibrationStatus?.[canvasImage?.id] ===
        ImageCalibrationStatus.CALIBRATING &&
      _type === imageType.RAW
    ) {
      if (canvasImage?.id) {
        dispatch(
          setImageCalibrationStatus({
            id: canvasImage?.id,
            status: ImageCalibrationStatus.CALIBRATED,
          }),
        )
      }
    }

    if (selectedDentinColorKey) {
      if (!dentinCeramicBrand) {
        toast.error(t('selectCeramicBrand'))
        return
      }
      dispatch(
        setDentinColorTarget({
          imageId: canvasImage?.id,
          targetcolor: [labColor.L, labColor.a, labColor.b],
          position: imagePos,
          output: {
            [selectedDentinColorKey]: selectedDentinColorKey,
          },
        }),
      )
      toast.info(translation('recipeGenerate.inProgress'))
      generateDentinRecipe({
        brand: dentinCeramicBrand,
        targetcolor: [labColor.L, labColor.a, labColor.b],
        protocol:
          _type === imageType.RAW
            ? `L${greyCardValue?.[canvasImage?.id] && greyCardValue?.[canvasImage?.id] !== '' ? greyCardValue?.[canvasImage?.id] : 79}`
            : 'Optishade',
      })
        .unwrap()
        .then((res) => {
          toast.success(t('recipeGenerated'))

          dispatch(
            setdentinRecipeOutput({
              imageId: canvasImage?.id,
              key: selectedDentinColorKey,
              result: res,
            }),
          )
        })
        .catch((error) => {
          toast.error(error?.data?.message || t('recipeGenerationFailed'))
        })
    }

    dispatch(setSelectedDentinColorKey(''))
  }

  const dentinColor = useAppSelector((state) => state.pfmCrown.dentinColor)

  const regenerateRecipe = (regionKey: string, labColor: LABColor) => {
    if (!dentinCeramicBrand) {
      toast.error(t('selectCeramicBrand'))
      return
    }

    if (!canvasImage?.id) return

    const currentTarget =
      dentinColor?.[canvasImage.id]?.[regionKey]?.targetcolor
    const newTarget = labColor[regionKey]

    if (
      !currentTarget ||
      currentTarget[0] !== newTarget[0] ||
      currentTarget[1] !== newTarget[1] ||
      currentTarget[2] !== newTarget[2]
    ) {
      dispatch(
        setDentinColorTarget({
          key: regionKey,
          imageId: canvasImage.id,
          targetcolor: newTarget,
          position: dentinColor?.[canvasImage.id]?.[regionKey]?.position,
          output: {
            [regionKey]: regionKey,
          },
        }),
      )
    }

    generateDentinRecipe({
      brand: dentinCeramicBrand,
      targetcolor: newTarget,
      protocol:
        canvasImage?.type === imageType.RAW
          ? `L${
              greyCardValue?.[canvasImage?.id] &&
              greyCardValue?.[canvasImage?.id] !== ''
                ? greyCardValue?.[canvasImage?.id]
                : 79
            }`
          : 'Optishade',
    })
      .unwrap()
      .then((res) => {
        toast.success(t('recipeGenerated'))

        dispatch(
          setdentinRecipeOutput({
            imageId: canvasImage.id,
            key: regionKey,
            result: res,
          }),
        )
      })
      .catch((error) => {
        toast.error(error?.data?.message || t('recipeGenerationFailed'))
      })
  }

  const onSaveAsImage = () => {
    generateImage(combinedRef, 'Dentin-Recipe.png')
  }

  const handleOpenSaveDialog = () => {
    setSaveRecipedialogOpen(true)
  }

  const handleSaveToProject = async (targetCaseId: string) => {
    await saveRecipeGeneric({
      caseId: targetCaseId,
      canvasImage,
      recipeName,
      output: prepareSelectedDentinOutput(
        dentinRecipeOutput?.[canvasImage?.id],
        dentinRecipeIndexByRegion?.[canvasImage?.id],
      ),
      workflow: Workflows?.PFM_CROWN,
      mutationFn: saveRecipe,
      closeDialog: () => setSaveRecipedialogOpen(false),
      recipeType: 'dentin',
    })
  }

  const onSaveAsPDF = async () => {
    await saveRecipeGeneric({
      caseId,
      canvasImage,
      recipeName,
      output: prepareSelectedDentinOutput(
        dentinRecipeOutput?.[canvasImage?.id],
        dentinRecipeIndexByRegion?.[canvasImage?.id],
      ),
      workflow: Workflows?.PFM_CROWN,
      mutationFn: savePdf,
      recipeType: 'dentin',
      isPdf: true,
    })
  }
  return (
    <CaseInfoLayout
      rightPanel={
        <DentinPanel
          containerRef={containerRef}
          regenerateRecipe={regenerateRecipe}
        />
      }
      disabled={hasNonEmptyOutputSingleCanvas(
        dentinRecipeOutput,
        canvasImage?.id,
      )}
      onSaveAsImage={onSaveAsImage}
      onSaveAsPDF={onSaveAsPDF}
      handleSaveToProject={handleOpenSaveDialog}
    >
      <Box display="flex" flexDirection="column" flexGrow={1}>
        <Box
          ref={containerRef}
          display="flex"
          flexGrow={1}
          flexDirection={'column'}
        >
          <WorkFlowTab width={dimensions.width} />
          {!imageEdit ? (
            <>
              <NewKonvaCanvas
                type={canvasImage?.type}
                key={canvasImage?.id || canvasImage?.image}
                imageUrl={
                  getDehazedImageUrl(canvasImage) ||
                  canvasImage?.latest_image ||
                  canvasImage?.calibrated_image ||
                  canvasImage?.image
                }
                onImageClick={onImageClick}
                width={dimensions.width}
                height={dimensions.height}
                imageId={canvasImage?.id}
                combinedRef={combinedRef}
                rawWidth={canvasImage?.raw_width}
                rawHeight={canvasImage?.raw_height}
                isActiveCircle={!!selectedDentinColorKey}
              />
            </>
          ) : (
            <ImageEdit selectedImage={canvasImage} />
          )}
        </Box>
      </Box>
      <SaveRecipeDialog
        saveRecipedialogOpen={saveRecipedialogOpen}
        setSaveRecipedialogOpen={setSaveRecipedialogOpen}
        recipeName={recipeName}
        setRecipeName={setRecipeName}
        onSave={handleSaveToProject}
        isLoading={isLoading}
      />
    </CaseInfoLayout>
  )
}

export default index
